import { motion, useScroll, useTransform, type MotionValue } from "framer-motion";
import { useRef } from "react";
import { achievements, type Achievement } from "../config/achievements";
import { devComments } from "../config/devComments";
import { HiddenDevComment } from "./DeveloperMode";
import "./Achievements.css";

// Sticky milestone card component. As the user scrolls, the card's scale shrinks from 1 to targetScale
// based on the scroll progress range. Each card is positioned at a staggered top offset.
function MilestoneCard({
  i,
  item,
  progress,
  range,
  targetScale,
}: {
  i: number;
  item: Achievement;
  progress: MotionValue<number>;
  range: [number, number];
  targetScale: number;
}) {
  // Maps scroll progress within the item's range to a scale between 1 and targetScale.
  // useTransform(progress, range, [1, targetScale]): transforms scroll progress into a scale value.
  // Range determines which portion of scroll controls this card's animation.
  const scale = useTransform(progress, range, [1, targetScale]);
  // Staggered starting Y position: mobile cards start at 20% viewport height, desktop at 30%.
  // The per-card 15px offset stacks cards slightly so they overlap, creating the sticky stack effect.
  // CRITICAL: This calculates at RENDER TIME. If window resizes, the value is stale until re-render.
  // The useScroll target (container ref) handles this partially, but individual card positions are
  // not reactive to resize.
  // A fixed 32px offset on narrow screens compensates for the smaller window.innerHeight
  // reported by real mobile browsers (browser chrome, status bars) vs DevTools emulation.
  const narrowOffset = window.innerWidth < 480 ? 15 : 0;
  const startY = (window.innerWidth < 768 ? 0.2 : 0.3) * window.innerHeight + i * 15 + narrowOffset;

  return (
    <div className="milestone-sticky">
      <motion.div
        style={{
          scale,
          top: `${startY}px`,
          "--milestone-color": item.color,
          "--milestone-color-alpha": `${item.color}44`,
        } as Record<string, unknown>}
        className="milestone-card"
      >
        <div className="milestone-card-accent" />
        <div className={`milestone-icon ${item.type}`}>
          {item.icon}
        </div>
        <div className="milestone-content">
          <span className="milestone-date">
            {item.date}
          </span>
          <h3>{item.title}</h3>
          <p className="milestone-desc">{item.description}</p>
        </div>
      </motion.div>
    </div>
  );
}

// Stacked achievements layout: cards are sticky-positioned and shrink on scroll
// using Framer Motion's useScroll and useTransform for parallax-like stacking effect
const AchievementsStack = () => {
  const container = useRef<HTMLDivElement>(null);
  // Tracks scroll progress through the entire achievements container
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"],
  });

  // CRITICAL architectural note: The sticky stack requires THREE things to work correctly:
  //   1. overflow: clip on .achievements-stack (NOT overflow: hidden -- clip does NOT create a
  //      scroll container per CSS spec, while hidden does, which breaks sticky positioning)
  //   2. Eager import of this component (no lazy loading) -- see Achievements.tsx comments
  //   3. body must NOT have overflow-x: hidden (that forces overflow-y: auto, making body a
  //      scroll container, breaking sticky)
  // If sticky scrolling breaks, check these three conditions first.
  return (
    <div id="achievements" ref={container} className="achievements-stack">
      <div className="achievements-stack-header">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Milestones & Impact
          <HiddenDevComment {...devComments.achievements} />
        </motion.h2>
      </div>
      {achievements.map((item, i) => {
        // targetScale = each card is 5% smaller than the one above it, capped at 50% minimum.
        // The (achievements.length - i - 1) means the first (top) card stays at 100%, subsequent
        // cards shrink. The 50% minimum prevents cards from becoming too small to read.
        const targetScale = Math.max(0.5, 1 - (achievements.length - i - 1) * 0.05);
        // range = [i * 0.1, 1]: each card maps a 10% chunk of scroll progress. Card 0: 0-100%,
        // Card 1: 10-100%, etc. The last cards have very small range windows and animate quickly.
        return <MilestoneCard key={i} i={i} item={item} progress={scrollYProgress} range={[i * 0.1, 1]} targetScale={targetScale} />;
      })}
    </div>
  );
};

export default AchievementsStack;
